import { useState, type ReactNode } from 'react';
import type { App, Project } from '@hangar/shared';
import type { Profile, ProviderAccount } from '@/api/schemas';
import { useCan, type Can } from '@/lib/permissions';
import type { RowAction } from '@/components/RowActions';
import { ArchiveProfileDialog } from './ArchiveProfileDialog';
import { ProfileSheet } from './ProfileSheet';

interface Options {
  app: App | undefined;
  project: Project | undefined;
  accounts: ProviderAccount[] | undefined;
}

export interface ProfileActions {
  /** `projects:write` on the app's project (hidden for guests, disabled with a reason for read-only roles). */
  can: Can;
  create: () => void;
  /** Edit · Duplicate · Archive for one row; a hangar.yml profile can only be duplicated. */
  actionsFor: (profile: Profile) => RowAction[];
  /** The sheet and the confirm dialog; render once next to the list. */
  overlays: ReactNode;
}

const MANAGED = 'Managed by hangar.yml';

/** Create, edit, duplicate (a prefilled create) and archive build profiles: one state for the Apps & profiles page and Getting started. */
export function useProfileActions({ app, project, accounts }: Options): ProfileActions {
  const can = useCan('projects:write', project?.id);
  const [sheet, setSheet] = useState<{ editing?: Profile; duplicateOf?: Profile } | null>(null);
  const [archiving, setArchiving] = useState<Profile | null>(null);
  const reason = can.allowed ? undefined : can.reason;

  const actionsFor = (p: Profile): RowAction[] => {
    if (can.hidden) return [];
    const managed = p.definedIn === 'config_file' ? MANAGED : undefined;
    return [
      { key: 'edit', label: 'Edit', icon: managed ? 'lock' : 'edit', testId: 'profile-edit', disabledReason: reason ?? managed, onSelect: () => setSheet({ editing: p }) },
      { key: 'duplicate', label: 'Duplicate', icon: 'content_copy', testId: 'profile-duplicate', disabledReason: reason, onSelect: () => setSheet({ duplicateOf: p }) },
      { key: 'archive', label: 'Archive', icon: 'archive', testId: 'profile-archive', destructive: true, disabledReason: reason ?? managed, onSelect: () => setArchiving(p) },
    ];
  };

  const overlays =
    app && project ? (
      <>
        <ProfileSheet open={Boolean(sheet)} onOpenChange={(o) => (o ? undefined : setSheet(null))} app={app} project={project} editing={sheet?.editing} duplicateOf={sheet?.duplicateOf} accounts={accounts} />
        <ArchiveProfileDialog profile={archiving} app={app} project={project} onOpenChange={(o) => (o ? undefined : setArchiving(null))} />
      </>
    ) : null;

  return { can, create: () => setSheet({}), actionsFor, overlays };
}
